import { type Player, PlayerStatus } from '@infiltre/shared';
import { useState } from 'react';
import { useT } from '@/shared/lib/i18n';
import { Button, PhaseHeader } from '@/shared/ui';
import { VoteCandidateGrid } from './vote/VoteCandidateGrid';

export type EliminatePickerProps = {
  players: readonly Player[];
  onPick: (targetId: string) => void;
};

/**
 * Standalone elimination picker. Two-step: tap polaroid → confirm via
 * CTA. Until a candidate is picked the CTA stays dark/disabled
 * ("CHOISIS L'ÉLIMINÉ"); once tapped it switches to orange + named
 * ("Éliminer {name} →"). Submission fires a single `EliminatePlayer`
 * message that the standalone driver treats as a unanimous synthetic
 * vote.
 */
export const EliminatePicker = ({ players, onPick }: EliminatePickerProps) => {
  const t = useT();
  const alive = players.filter((p) => p.status === PlayerStatus.Alive);
  const [picked, setPicked] = useState<string | null>(null);
  const pickedPlayer = alive.find((p) => p.id === picked) ?? null;

  const submit = (): void => {
    if (!picked) return;
    onPick(picked);
    setPicked(null);
  };

  return (
    <main className="mx-auto flex h-full w-full max-w-3xl flex-col">
      <PhaseHeader
        eyebrow={t('round.vote.phase_eyebrow')}
        title={t('round.vote.title')}
        descriptor={t('round.vote.descriptor')}
      />

      <div className="flex flex-1 flex-col items-center px-5 pt-6 pb-3">
        <VoteCandidateGrid candidates={alive} pickedId={picked} onPick={setPicked} />
      </div>

      <div className="px-5 pt-2 pb-5">
        <Button variant="orange" onClick={submit} disabled={!pickedPlayer}>
          {pickedPlayer
            ? t('round.eliminate.cta_picked', { name: pickedPlayer.name })
            : t('round.eliminate.cta_idle')}
        </Button>
      </div>
    </main>
  );
};
